// /performance/visitors — ported 1:1 from miluliem's
// apps/admin/src/pages/admin/Visitors.tsx. Keep the rendering shape +
// industry benchmarks + funnel logic aligned when changing.
//
// Adaptations for vinoga:
//   - URLs go through the shared api() helper instead of a raw
//     getToken() call.
//   - DateRangePicker → DateRangeSelect (vinoga uses presets only;
//     full calendar lands as a follow-up).

import { useEffect, useMemo, useState, useCallback } from "react";
import {
  Eye,
  ShoppingCart,
  CreditCard,
  Smartphone,
  Tablet,
  Monitor,
  HelpCircle,
  ChevronLeft,
  ChevronRight,
  Globe,
  ExternalLink,
  Check,
  X,
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import DateRangeSelect, {
  type DateRange,
  rangeForPreset,
  rangeToQuery,
} from "@/components/admin/DateRangeSelect";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { api } from "@/lib/api";

type DeviceTab = "all" | "mobile" | "tablet" | "desktop";

interface FunnelSteps {
  visited: number;
  added_to_cart: number;
  viewed_cart: number;
  began_checkout: number;
  payment_initiated: number;
  purchased: number;
  conversion_rate: number | null;
}

interface FunnelByDevice {
  range?: { from: string | null; to: string | null; label: string };
  all: FunnelSteps;
  by_device: {
    mobile: FunnelSteps;
    tablet: FunnelSteps;
    desktop: FunnelSteps;
    unknown: FunnelSteps;
  };
}

interface SessionRow {
  session_id: string;
  first_seen: string;
  last_seen: string;
  page_views: number;
  device_type: string | null;
  browser: string | null;
  os: string | null;
  country_code: string | null;
  city: string | null;
  first_referrer: string | null;
  funnel: {
    visited: boolean;
    added_to_cart: boolean;
    viewed_cart: boolean;
    began_checkout: boolean;
    payment_initiated: boolean;
    purchased: boolean;
  };
  cart_total: number | null;
  order: {
    id: string;
    order_number: string;
    total_amount: number;
    payment_status: string;
  } | null;
}

const INDUSTRY_CR: Record<DeviceTab, number> = {
  all: 0.025,
  mobile: 0.018,
  tablet: 0.025,
  desktop: 0.03,
};

const PAGE_SIZE = 50;

export function Visitors() {
  const [range, setRange] = useState<DateRange>(() => rangeForPreset("last_7d"));
  const [deviceTab, setDeviceTab] = useState<DeviceTab>("all");
  const [funnelData, setFunnelData] = useState<FunnelByDevice | null>(null);
  const [funnelLoading, setFunnelLoading] = useState(true);
  const [sessions, setSessions] = useState<SessionRow[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);

  const fetchFunnel = useCallback(async () => {
    setFunnelLoading(true);
    try {
      const { from, to } = rangeToQuery(range);
      const json = await api<{ data: FunnelByDevice }>(
        `/api/v1/admin/visitors/funnel-by-device?from=${from}&to=${to}`,
      );
      setFunnelData(json.data ?? null);
    } catch (err) {
      console.error("[Visitors] funnel fetch failed", err);
    }
    setFunnelLoading(false);
  }, [range]);

  const fetchSessions = useCallback(async () => {
    setSessionsLoading(true);
    try {
      const { from, to } = rangeToQuery(range);
      const params = new URLSearchParams({
        from,
        to,
        device: deviceTab,
        page: String(page),
        limit: String(PAGE_SIZE),
      });
      const json = await api<{
        data: SessionRow[];
        meta: { total: number };
      }>(`/api/v1/admin/visitors/sessions?${params}`);
      setSessions(json.data ?? []);
      setTotalCount(json.meta?.total ?? 0);
    } catch (err) {
      console.error("[Visitors] sessions fetch failed", err);
    }
    setSessionsLoading(false);
  }, [range, deviceTab, page]);

  useEffect(() => {
    fetchFunnel();
  }, [fetchFunnel]);
  useEffect(() => {
    setPage(0);
  }, [range, deviceTab]);
  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const activeFunnel = useMemo<FunnelSteps | null>(() => {
    if (!funnelData) return null;
    if (deviceTab === "all") return funnelData.all;
    return funnelData.by_device[deviceTab];
  }, [funnelData, deviceTab]);

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="flex items-center gap-2 font-display text-xl font-bold text-foreground">
            <Eye size={20} className="text-blue-500" />
            Visitors
          </h1>
          <p className="mt-1 font-body text-xs text-muted-foreground">
            First-party conversion funnel for vinoga.lv
          </p>
        </div>
        <DateRangeSelect value={range} onChange={setRange} />
      </div>

      <section>
        <Tabs value={deviceTab} onValueChange={(v) => setDeviceTab(v as DeviceTab)}>
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-display text-sm font-semibold uppercase tracking-wider text-muted-foreground">
              Conversion Funnel
            </h2>
            <TabsList className="bg-muted">
              <TabsTrigger value="all" className="font-body text-xs">
                All devices
              </TabsTrigger>
              <TabsTrigger value="mobile" className="font-body text-xs">
                <Smartphone size={12} className="mr-1" /> Mobile
              </TabsTrigger>
              <TabsTrigger value="tablet" className="font-body text-xs">
                <Tablet size={12} className="mr-1" /> Tablet
              </TabsTrigger>
              <TabsTrigger value="desktop" className="font-body text-xs">
                <Monitor size={12} className="mr-1" /> Desktop
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value={deviceTab} className="mt-0">
            {funnelLoading || !activeFunnel ? (
              <div className="p-6 font-body text-sm text-muted-foreground">Loading funnel…</div>
            ) : (
              <FunnelChart steps={activeFunnel} deviceTab={deviceTab} />
            )}
          </TabsContent>
        </Tabs>
      </section>

      <section>
        <h2 className="mb-3 font-display text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          Sessions
          {totalCount > 0 && (
            <span className="ml-2 font-body text-xs font-normal normal-case">
              ({totalCount.toLocaleString()})
            </span>
          )}
        </h2>
        {sessionsLoading ? (
          <div className="p-4 font-body text-sm text-muted-foreground">Loading…</div>
        ) : sessions.length === 0 ? (
          <div className="py-16 text-center text-muted-foreground">
            <Eye size={40} className="mx-auto mb-3 opacity-30" />
            <p className="font-body text-sm">No sessions recorded for this window.</p>
          </div>
        ) : (
          <>
            <div className="overflow-hidden rounded-lg border border-border bg-card">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="font-bold">Session</TableHead>
                    <TableHead className="font-bold">Device</TableHead>
                    <TableHead className="font-bold">Browser / OS</TableHead>
                    <TableHead className="font-bold">Location</TableHead>
                    <TableHead className="text-center font-bold">Pages</TableHead>
                    <TableHead className="font-bold">Funnel</TableHead>
                    <TableHead className="text-right font-bold">Cart</TableHead>
                    <TableHead className="font-bold">Order</TableHead>
                    <TableHead className="font-bold">Last seen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sessions.map((s) => (
                    <SessionTableRow key={s.session_id} session={s} />
                  ))}
                </TableBody>
              </Table>
            </div>
            {totalPages > 1 && (
              <div className="mt-3 flex items-center justify-between px-1">
                <span className="font-body text-xs text-muted-foreground">
                  Showing {page * PAGE_SIZE + 1}–
                  {Math.min((page + 1) * PAGE_SIZE, totalCount)} of {totalCount.toLocaleString()}
                </span>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 px-2"
                    disabled={page === 0}
                    onClick={() => setPage(0)}
                  >
                    «
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 px-2"
                    disabled={page === 0}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    <ChevronLeft size={14} />
                  </Button>
                  <span className="px-3 font-body text-xs text-foreground">
                    Page {page + 1} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 px-2"
                    disabled={page >= totalPages - 1}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    <ChevronRight size={14} />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 px-2"
                    disabled={page >= totalPages - 1}
                    onClick={() => setPage(totalPages - 1)}
                  >
                    »
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}

function FunnelChart({ steps, deviceTab }: { steps: FunnelSteps; deviceTab: DeviceTab }) {
  const stages: { key: keyof FunnelSteps; label: string; icon: typeof Eye; color: string }[] = [
    { key: "added_to_cart", label: "Added to cart", icon: ShoppingCart, color: "bg-violet-500" },
    { key: "viewed_cart", label: "Viewed cart", icon: ShoppingCart, color: "bg-fuchsia-500" },
    { key: "began_checkout", label: "Began checkout", icon: CreditCard, color: "bg-amber-500" },
    { key: "payment_initiated", label: "Payment initiated", icon: CreditCard, color: "bg-orange-500" },
    { key: "purchased", label: "Purchased", icon: Check, color: "bg-emerald-500" },
  ];

  const visited = steps.visited || 0;
  const cr = steps.conversion_rate;
  const benchmark = INDUSTRY_CR[deviceTab];
  const crVsBench = cr !== null ? (cr - benchmark) * 100 : null;

  const MIN_SCALE_PCT = 8;
  const maxStepPctOfVisited =
    visited > 0
      ? Math.max(...stages.map((s) => ((steps[s.key] as number) || 0) / visited * 100))
      : MIN_SCALE_PCT;
  const chartScalePct = Math.max(MIN_SCALE_PCT, Math.ceil(maxStepPctOfVisited));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between rounded-lg border border-border bg-card p-4">
        <div>
          <div className="font-body text-xs uppercase tracking-wider text-muted-foreground">
            Conversion rate
          </div>
          <div className="mt-1 font-display text-3xl font-bold text-foreground">
            {cr !== null ? `${(cr * 100).toFixed(2)}%` : "—"}
          </div>
        </div>
        <div className="text-right">
          <div className="font-body text-xs text-muted-foreground">
            Industry benchmark{deviceTab !== "all" ? ` (${deviceTab})` : ""}
          </div>
          <div className="mt-1 font-body text-sm text-foreground">
            {(benchmark * 100).toFixed(1)}%
            {crVsBench !== null && (
              <Badge
                variant={crVsBench >= 0 ? "default" : "destructive"}
                className="ml-2 text-[10px]"
              >
                {crVsBench >= 0 ? "+" : ""}
                {crVsBench.toFixed(2)}pp
              </Badge>
            )}
          </div>
        </div>
      </div>

      <div className="space-y-2 rounded-lg border border-border bg-card p-4">
        <div className="flex items-center gap-3 border-b border-border/50 pb-2">
          <div className="flex w-44 shrink-0 items-center gap-2">
            <Eye size={14} className="text-blue-500" />
            <span className="font-body text-sm text-muted-foreground">Visited (denominator)</span>
          </div>
          <div className="flex-1 font-body text-xs text-muted-foreground">
            {visited.toLocaleString()} unique sessions
            <span className="ml-2 text-muted-foreground/60">
              · bars scaled to {chartScalePct}% max
            </span>
          </div>
        </div>
        {stages.map((stage, i) => {
          const value = (steps[stage.key] as number) || 0;
          const pctOfVisited = visited > 0 ? (value / visited) * 100 : 0;
          const barFillPct =
            chartScalePct > 0 ? Math.min(100, (pctOfVisited / chartScalePct) * 100) : 0;
          const prevValue =
            i === 0 ? visited : (steps[stages[i - 1]!.key] as number) || 0;
          const dropoffPct = prevValue > 0 ? ((prevValue - value) / prevValue) * 100 : null;
          const Icon = stage.icon;

          return (
            <div key={stage.key} className="group flex items-center gap-3">
              <div className="flex w-44 shrink-0 items-center gap-2">
                <Icon size={14} className="text-muted-foreground" />
                <span className="font-body text-sm text-foreground">{stage.label}</span>
              </div>
              <div className="relative h-7 flex-1 rounded bg-muted">
                <div
                  className={`${stage.color} h-full rounded transition-all`}
                  style={{ width: `${barFillPct}%` }}
                />
                <div className="absolute inset-0 flex items-center px-2">
                  <span className="font-body text-xs font-semibold text-foreground mix-blend-difference">
                    {value.toLocaleString()}
                  </span>
                </div>
              </div>
              <div className="w-20 text-right">
                <span className="font-body text-xs text-muted-foreground">
                  {pctOfVisited.toFixed(1)}%
                </span>
              </div>
              <div className="w-24 text-right">
                {dropoffPct !== null && dropoffPct > 0 ? (
                  <span
                    className={`font-body text-xs ${
                      dropoffPct > 50 ? "text-red-500" : "text-muted-foreground"
                    }`}
                  >
                    −{dropoffPct.toFixed(1)}%
                  </span>
                ) : (
                  <span className="font-body text-xs text-muted-foreground/40">—</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SessionTableRow({ session }: { session: SessionRow }) {
  const stages: { key: keyof SessionRow["funnel"]; label: string }[] = [
    { key: "visited", label: "Visited" },
    { key: "added_to_cart", label: "Added to cart" },
    { key: "viewed_cart", label: "Viewed cart" },
    { key: "began_checkout", label: "Began checkout" },
    { key: "payment_initiated", label: "Payment initiated" },
    { key: "purchased", label: "Purchased" },
  ];

  const highestReachedIdx = stages.reduce(
    (max, stage, i) => (session.funnel[stage.key] ? i : max),
    -1,
  );
  const reachedThrough = (i: number) => i <= highestReachedIdx;

  const DeviceIcon =
    session.device_type === "mobile"
      ? Smartphone
      : session.device_type === "tablet"
        ? Tablet
        : session.device_type === "desktop"
          ? Monitor
          : HelpCircle;

  const lastSeen = new Date(session.last_seen);
  const diffMin = (Date.now() - lastSeen.getTime()) / 60_000;
  const lastSeenLabel =
    diffMin < 60
      ? `${Math.round(diffMin)}m ago`
      : diffMin < 60 * 24
        ? `${Math.round(diffMin / 60)}h ago`
        : lastSeen.toLocaleDateString("lv", { day: "2-digit", month: "short" });

  return (
    <TableRow>
      <TableCell className="py-3 font-mono text-xs text-muted-foreground">
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="cursor-help">{session.session_id.slice(0, 8)}…</span>
            </TooltipTrigger>
            <TooltipContent>
              <div className="font-mono text-[11px]">{session.session_id}</div>
              {session.first_referrer && (
                <div className="mt-1 text-[10px] text-muted-foreground">
                  From: {session.first_referrer.slice(0, 80)}
                </div>
              )}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </TableCell>
      <TableCell className="py-3">
        <div className="flex items-center gap-1.5">
          <DeviceIcon size={13} className="text-muted-foreground" />
          <span className="font-body text-xs capitalize text-foreground">
            {session.device_type ?? "unknown"}
          </span>
        </div>
      </TableCell>
      <TableCell className="py-3 font-body text-xs text-muted-foreground">
        {session.browser ?? "—"} / {session.os ?? "—"}
      </TableCell>
      <TableCell className="py-3 font-body text-xs text-muted-foreground">
        <div className="flex items-center gap-1.5">
          <Globe size={11} />
          {[session.city, session.country_code].filter(Boolean).join(", ") || "—"}
        </div>
      </TableCell>
      <TableCell className="py-3 text-center font-body text-xs text-foreground">
        {session.page_views}
      </TableCell>
      <TableCell className="py-3">
        <div className="flex items-center gap-0.5">
          {stages.map((stage, i) => {
            const reached = reachedThrough(i);
            const inferred = reached && !session.funnel[stage.key];
            return (
              <TooltipProvider key={stage.key} delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div
                      className={`flex h-3.5 w-3.5 items-center justify-center rounded-sm ${
                        reached ? "bg-emerald-500" : "bg-muted"
                      } ${inferred ? "opacity-50" : ""}`}
                    >
                      {reached ? (
                        <Check size={9} className="text-white" />
                      ) : (
                        <X size={9} className="text-muted-foreground/30" />
                      )}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent className="text-xs">
                    {stage.label}: {reached ? "✓" : "—"}
                    {inferred && " (inferred from later step)"}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            );
          })}
        </div>
      </TableCell>
      <TableCell className="py-3 text-right font-body text-xs">
        {session.cart_total !== null ? (
          <span className={session.order ? "text-foreground" : "text-amber-600 dark:text-amber-400"}>
            €
            {session.cart_total.toLocaleString("en", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        ) : (
          <span className="text-muted-foreground/40">—</span>
        )}
      </TableCell>
      <TableCell className="py-3">
        {session.order ? (
          <a
            href={`/orders/${session.order.id}`}
            className="inline-flex items-center gap-1 font-body text-xs text-foreground hover:text-primary"
          >
            {session.order.order_number}
            <ExternalLink size={10} />
          </a>
        ) : (
          <span className="font-body text-xs text-muted-foreground/40">—</span>
        )}
      </TableCell>
      <TableCell className="py-3 font-body text-xs text-muted-foreground">{lastSeenLabel}</TableCell>
    </TableRow>
  );
}
